import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';

import { MainPageWithButtons, TextInputComponent, CapsuleButton, WaitSpinner } from "../_components";
import { LicensesActions } from "../_actions/LicensesActions";
import { BudgetsUiAlertsActions } from "../_actions/BudgetsUiAlertsActions";
import { genericActions } from "../_helpers/ActionErrorWrapper"
import { getSellableLicenseTypes } from "./getSellableLicenseTypes";
import { LicenseTypeSelector } from "./LicenseTypeSelector";
import { isClockinsLicenseType, VirtualClockinCheckbox } from "./VirtualClockinCheckbox";

export function bulkAssignResultMessage(t, result) {
    const leftOut = [...result.skippedLocationIds, ...result.notFoundLocationIds];
    if (leftOut.length === 0)
        return t("mod_budgets_bulk_success", { count: result.assigned.length });
    return t("mod_budgets_bulk_success_partial", {
        count: result.assigned.length,
        leftOut: leftOut.join(", "),
    });
}

export function BulkAssignLicenseView({licenseTypes, onAssignSuccess}) {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //LOCAL STATE
    const [email, setEmail] = useState("");
    const [licenseTypeUID, setLicenseTypeUID] = useState("");
    const [createVirtualClockin, setCreateVirtualClockin] = useState(false);
    const [quantity, setQuantity] = useState("1");
    const [locations, setLocations] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [filter, setFilter] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);

    // Monotonic tag for the locations lookup: the load button is disabled
    // while it flies but the email input is not, so editing the email mid
    // flight (which already drops the list) could be undone by the response
    // of the abandoned email landing afterwards, leaving the new email on
    // screen next to the previous admin's locations. Every lookup and every
    // email edit bumps the tag; a response whose tag is no longer the current
    // one is discarded. Same discard-stale-by-tag pattern as the available
    // locations store in Organizations.
    const loadRequestIdRef = useRef(0);

    //VIEW

    const filteredLicenseTypes = getSellableLicenseTypes(licenseTypes);

    const hasTypeActive = (location) =>
        licenseTypeUID !== "" &&
        location.activeLicenseTypeUids.includes(licenseTypeUID);

    const selectableLocations = (locations ?? []).filter(
        location => !hasTypeActive(location)
    );

    const visibleLocations = (locations ?? []).filter(location => {
        const term = filter.trim().toLowerCase();
        if (!term)
            return true;
        return location.locationName.toLowerCase().includes(term)
            || String(location.locationId).includes(term);
    });

    const selectedCount = selectableLocations
        .filter(location => selectedIds.has(location.locationId))
        .length;

    return (
        <MainPageWithButtons
            pageTitle={t("mod_budgets_assign_bulk")}
            content={content()}
        />
    );

    function content() {
        return (
            <div>
                <div className="mt-2"/>
                <div className="font-weight-bold text-uppercase">
                    {t("mod_budgets_license_type")}
                </div>
                <LicenseTypeSelector
                    licenseTypes={filteredLicenseTypes}
                    value={licenseTypeUID}
                    onChange={handleLicenseTypeChange}
                />
                {isClockinsLicenseType(filteredLicenseTypes, licenseTypeUID) &&
                    <VirtualClockinCheckbox
                        checked={createVirtualClockin}
                        onChange={setCreateVirtualClockin}
                    />
                }
                <div className="mt-2"/>
                <div className="font-weight-bold text-uppercase">
                    {t("mod_budgets_owner_email")}
                </div>
                <div className="d-flex align-items-center">
                    <div className="flex-grow-1 mr-2">
                        <TextInputComponent
                            testID={"bulk-assign-email"}
                            title={t("global_email")}
                            value={email}
                            onChange={handleEmailChange}
                        />
                    </div>
                    <CapsuleButton
                        testId={"bulk-assign-load-locations"}
                        onClick={handleLoadLocations}
                        text={t("mod_budgets_bulk_load_locations")}
                        isEnabled={email.trim() !== "" && !isLoading}
                    />
                </div>
                <div className="mt-2"/>
                <div className="font-weight-bold text-uppercase">
                    {t("mod_budgets_quantity")}
                </div>
                <TextInputComponent
                    title={t("mod_budgets_quantity")}
                    value={quantity}
                    onChange={setQuantity}
                />
                <div className="mt-3"/>
                {locationsSection()}
                <div className="mt-4"/>
                <CapsuleButton
                    testId={"bulk-assign-submit"}
                    isEnabled={canAssign()}
                    onClick={handleAssignBulk}
                    text={t("mod_budgets_assign")}
                />
            </div>
        );
    }

    function locationsSection() {
        if (isLoading)
            return <WaitSpinner />;

        if (locations == null)
            return null;

        if (locations.length === 0)
            return (
                <div className="alert alert-warning">
                    {t("mod_budgets_bulk_no_locations")}
                </div>
            );

        return (
            <div>
                <div className="font-weight-bold text-uppercase">
                    {t("mod_budgets_bulk_locations")}
                    {" · "}
                    {t("mod_budgets_bulk_selected_count", {
                        selected: selectedCount,
                        total: selectableLocations.length,
                    })}
                </div>
                <div className="my-2">
                    <TextInputComponent
                        title={t("mod_budgets_bulk_filter_placeholder")}
                        value={filter}
                        onChange={setFilter}
                    />
                </div>
                <div style={{ maxHeight: "20rem", overflowY: "auto" }}>
                    {visibleLocations.map(locationRow)}
                </div>
            </div>
        );
    }

    function locationRow(location) {
        const alreadyActive = hasTypeActive(location);
        return (
            <div key={location.locationId} className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id={`bulk-loc-${location.locationId}`}
                    checked={!alreadyActive && selectedIds.has(location.locationId)}
                    disabled={alreadyActive}
                    onChange={() => handleToggleLocation(location.locationId)}
                />
                <label
                    className="form-check-label"
                    htmlFor={`bulk-loc-${location.locationId}`}
                >
                    {`${location.locationName} (${location.locationId})`}
                    {alreadyActive &&
                        <span className="badge badge-secondary ml-2">
                            {t("mod_budgets_bulk_already_active")}
                        </span>
                    }
                </label>
            </div>
        );
    }

    function canAssign() {
        const parsedQuantity = Number(quantity);
        if (!licenseTypeUID || isAssigning || selectedCount === 0)
            return false;
        if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1)
            return false;
        return true;
    }

    //ACTION HANDLERS
    function handleLicenseTypeChange(uid) {
        setLicenseTypeUID(uid);
        setCreateVirtualClockin(isClockinsLicenseType(filteredLicenseTypes, uid));
    }

    function handleEmailChange(value) {
        setEmail(value);
        // The loaded list belongs to the previous email; drop it so the
        // assignment can never mix one admin's list with another's email
        setLocations(null);
        setSelectedIds(new Set());
        // ...and invalidate any lookup still in flight, which would otherwise
        // repopulate the list right after this cleared it.
        loadRequestIdRef.current++;
    }

    function handleToggleLocation(locationId) {
        setSelectedIds(previous => {
            const next = new Set(previous);
            if (next.has(locationId))
                next.delete(locationId);
            else
                next.add(locationId);
            return next;
        });
    }

    async function handleLoadLocations() {
        const requestId = ++loadRequestIdRef.current;
        setIsLoading(true);
        setLocations(null);
        setFilter("");
        try {
            const adminLocations = await dispatch(
                LicensesActions.getAdminLocations(email.trim())
            );
            // Stale response: the email was edited while this was in flight,
            // so these locations belong to an email nobody is looking at
            if (loadRequestIdRef.current !== requestId)
                return;
            // The generic wrapper resolves to undefined on REQUEST_EXPIRED;
            // keep the "not loaded" state instead of showing an empty list
            if (adminLocations == null)
                return;
            setLocations(adminLocations);
            // Preselect everything; already-active ones are filtered out per
            // selected type at render/submit time
            setSelectedIds(new Set(
                adminLocations.map(location => location.locationId)
            ));
        } catch (error) {
            dispatch(genericActions.errorCatch(error));
        } finally {
            setIsLoading(false);
        }
    }

    async function handleAssignBulk() {
        const locationIds = selectableLocations
            .filter(location => selectedIds.has(location.locationId))
            .map(location => location.locationId);

        setIsAssigning(true);
        dispatch(LicensesActions.assignLicenseBulk({
            email: email.trim(),
            licenseTypeUID,
            quantity: Number(quantity),
            locationIds,
            createVirtualClockin,
        })).then((result) => {
            // The generic wrapper resolves to undefined on REQUEST_EXPIRED
            if (result == null)
                return;
            dispatch(LicensesActions.changeSecondaryView(null));
            dispatch(BudgetsUiAlertsActions.showSuccessAlert(
                bulkAssignResultMessage(t, result)
            ));
            onAssignSuccess(result);
        }).catch((error) => {
            dispatch(genericActions.errorCatch(error));
        }).finally(() => {
            setIsAssigning(false);
        });
    }
}
